import {
  FeeSnapshot,
  FeeSnapshotItem,
  FeeTier,
  FlowDirection,
  normalizeFeeSnapshotResponse,
} from '@gatewee/types/fee';
import {Withdrawal} from '@gatewee/types/withdrawal';

export interface WithdrawalAmountBreakdown {
  amount: number;
  calculatedFee: number;
  finalAmount: number;
}

export type WithdrawalAmountInputMode = 'addFees' | 'deductFees';

function calculateDeductFeesAmounts(
  normalizedAmount: number,
  cashOutFee: FeeSnapshotItem | null,
): WithdrawalAmountBreakdown {
  const calculatedFee = cashOutFee
    ? calculateFeeFromSnapshotItem(cashOutFee, normalizedAmount)
    : 0;
  const finalAmount = roundCurrency(
    Math.max(0, normalizedAmount - calculatedFee),
  );

  return {
    amount: normalizedAmount,
    calculatedFee,
    finalAmount,
  };
}

function calculateAddFeesAmounts(
  desiredFinal: number,
  cashOutFee: FeeSnapshotItem | null,
): WithdrawalAmountBreakdown {
  if (!cashOutFee || desiredFinal <= 0) {
    return {
      amount: desiredFinal,
      calculatedFee: 0,
      finalAmount: desiredFinal,
    };
  }

  let gross = desiredFinal;
  let fee = 0;

  for (let i = 0; i < 20; i++) {
    fee = calculateFeeFromSnapshotItem(cashOutFee, gross);
    const nextGross = roundCurrency(desiredFinal + fee);
    if (Math.abs(nextGross - gross) < 0.001) {
      gross = nextGross;
      break;
    }
    gross = nextGross;
  }

  fee = calculateFeeFromSnapshotItem(cashOutFee, gross);
  const finalAmount = roundCurrency(Math.max(0, gross - fee));

  return {
    amount: gross,
    calculatedFee: fee,
    finalAmount:
      Math.abs(finalAmount - desiredFinal) < 0.02 ? desiredFinal : finalAmount,
  };
}

interface FeeParts {
  fixed: number | null;
  rate: number | null;
}

function toNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const parsed = Number(String(value).replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

function toMeaningfulNumber(value: unknown): number | null {
  const parsed = toNumberOrNull(value);
  if (parsed === null || parsed === 0) {
    return null;
  }
  return parsed;
}

function roundCurrency(value: number): number {
  return Math.round(value * 100) / 100;
}

function sortTiers(tiers: FeeTier[]): FeeTier[] {
  return [...tiers].sort((a, b) => a.minAmount - b.minAmount);
}

function extractLegacyFeeParts(fee: FeeSnapshotItem): FeeParts {
  const fixedAlias =
    fee.fixedAmount ??
    fee.fixedValue ??
    (fee.type === 'amount' ? fee.value : null);
  const rateAlias =
    fee.rateValue ?? fee.fixedRate ?? (fee.type === 'rate' ? fee.value : null);

  return {
    fixed: toMeaningfulNumber(fixedAlias),
    rate: toMeaningfulNumber(rateAlias),
  };
}

function partsFromTier(tier: FeeTier): FeeParts {
  return {
    fixed: toMeaningfulNumber(tier.fixedAmount),
    rate: toMeaningfulNumber(tier.rateValue),
  };
}

function mergeFeeParts(domain: FeeParts, recipient: FeeParts | null): FeeParts {
  if (!recipient) {
    return domain;
  }
  return {
    fixed: sumNullable(domain.fixed, recipient.fixed),
    rate: sumNullable(domain.rate, recipient.rate),
  };
}

function sumNullable(a: number | null, b: number | null): number | null {
  if (a === null && b === null) {
    return null;
  }
  return (a ?? 0) + (b ?? 0);
}

function mergeTierBands(
  domainTiers: FeeTier[],
  recipientTiers: FeeTier[],
): FeeTier[] {
  const map = new Map<string, FeeTier>();
  const key = (t: FeeTier) => `${t.minAmount}|${t.maxAmount ?? 'null'}`;

  for (const tier of domainTiers) {
    map.set(key(tier), {...tier});
  }
  for (const tier of recipientTiers) {
    const k = key(tier);
    const existing = map.get(k);
    if (existing) {
      map.set(k, {
        ...existing,
        fixedAmount: (existing.fixedAmount ?? 0) + (tier.fixedAmount ?? 0),
        rateValue: (existing.rateValue ?? 0) + (tier.rateValue ?? 0),
      });
    } else {
      map.set(k, {...tier});
    }
  }

  return Array.from(map.values()).sort((a, b) => a.minAmount - b.minAmount);
}

function mergeSnapshotItems(
  domain: FeeSnapshotItem,
  recipient: FeeSnapshotItem,
): FeeSnapshotItem {
  const domainTiers = sortTiers(domain.tiers ?? []);
  const recipientTiers = sortTiers(recipient.tiers ?? []);

  if (domainTiers.length > 0 || recipientTiers.length > 0) {
    return {
      ...domain,
      tiers: mergeTierBands(domainTiers, recipientTiers),
    };
  }

  const mergedParts = mergeFeeParts(
    extractLegacyFeeParts(domain),
    extractLegacyFeeParts(recipient),
  );

  return {
    ...domain,
    fixedAmount: mergedParts.fixed ?? undefined,
    rateValue: mergedParts.rate ?? undefined,
    value:
      mergedParts.fixed !== null
        ? mergedParts.fixed
        : mergedParts.rate !== null
        ? mergedParts.rate
        : domain.value,
  };
}

function asArray(
  items: FeeSnapshotItem[] | undefined | null,
): FeeSnapshotItem[] {
  return items ?? [];
}

function resolveEffectiveCashOutFee(
  snapshot: FeeSnapshot | FeeSnapshotItem[] | null | undefined,
): FeeSnapshotItem | null {
  if (!snapshot) {
    return null;
  }

  const snap = normalizeFeeSnapshotResponse(snapshot);
  const domain = asArray(snap.domain);
  const recipient = asArray(snap.recipient);

  const domainCashOut = domain.find(
    item => item.operation === FlowDirection.CASH_OUT,
  );
  const recipientCashOut = recipient.find(
    item => item.operation === FlowDirection.CASH_OUT,
  );

  if (domainCashOut && recipientCashOut) {
    return mergeSnapshotItems(domainCashOut, recipientCashOut);
  }
  return domainCashOut ?? recipientCashOut ?? null;
}

function feeFromParts(parts: FeeParts, amount: number): number {
  const fixed = parts.fixed ?? 0;
  const rate = parts.rate ?? 0;
  return roundCurrency(fixed + (amount * rate) / 100);
}

function findTierForAmount(tiers: FeeTier[], amount: number): FeeTier | null {
  const sorted = sortTiers(tiers);
  if (!sorted.length) {
    return null;
  }

  for (const tier of sorted) {
    const withinMin = amount >= tier.minAmount;
    const withinMax =
      tier.maxAmount === null ||
      tier.maxAmount === undefined ||
      amount <= tier.maxAmount;
    if (withinMin && withinMax) {
      return tier;
    }
  }

  const catchAll = sorted.find(
    tier => tier.maxAmount === null || tier.maxAmount === undefined,
  );
  return catchAll ?? sorted[sorted.length - 1];
}

function calculateFeeFromSnapshotItem(
  fee: FeeSnapshotItem,
  amount: number,
): number {
  const tiers = sortTiers(fee.tiers ?? []);
  if (tiers.length > 0) {
    const tier = findTierForAmount(tiers, amount);
    if (!tier) {
      return 0;
    }
    return feeFromParts(partsFromTier(tier), amount);
  }

  return feeFromParts(extractLegacyFeeParts(fee), amount);
}

/**
 * Estimates withdrawal fee breakdown from a recipient fee snapshot.
 * - addFees (default): input is desired recipient payout; fee is added on top for wallet debit.
 * - deductFees: input is wallet debit; fee is deducted to compute finalAmount.
 */
export function calculateWithdrawalAmounts(
  amount: number,
  snapshot: FeeSnapshot | FeeSnapshotItem[] | null | undefined,
  mode: WithdrawalAmountInputMode = 'addFees',
): WithdrawalAmountBreakdown {
  const normalizedInput = roundCurrency(Math.max(0, Number(amount) || 0));
  const cashOutFee = resolveEffectiveCashOutFee(snapshot);

  if (mode === 'deductFees') {
    return calculateDeductFeesAmounts(normalizedInput, cashOutFee);
  }

  return calculateAddFeesAmounts(normalizedInput, cashOutFee);
}

/** Prefer API fields on a persisted withdrawal over client-side estimates. */
export function resolveWithdrawalAmounts(
  withdrawal:
    | Pick<Withdrawal, 'amount' | 'calculatedFee' | 'finalAmount'>
    | null
    | undefined,
  snapshot?: FeeSnapshot | FeeSnapshotItem[] | null,
): WithdrawalAmountBreakdown | null {
  if (!withdrawal) {
    return null;
  }

  const amount = roundCurrency(Number(withdrawal.amount) || 0);
  const hasApiFee =
    withdrawal.calculatedFee !== null && withdrawal.calculatedFee !== undefined;
  const hasApiFinal =
    withdrawal.finalAmount !== null && withdrawal.finalAmount !== undefined;

  if (hasApiFee || hasApiFinal) {
    const calculatedFee = hasApiFee
      ? roundCurrency(Number(withdrawal.calculatedFee) || 0)
      : snapshot
      ? calculateWithdrawalAmounts(amount, snapshot, 'deductFees').calculatedFee
      : 0;
    const finalAmount = hasApiFinal
      ? roundCurrency(Number(withdrawal.finalAmount) || 0)
      : roundCurrency(Math.max(0, amount - calculatedFee));

    return {amount, calculatedFee, finalAmount};
  }

  if (snapshot) {
    return calculateWithdrawalAmounts(amount, snapshot, 'deductFees');
  }

  return {amount, calculatedFee: 0, finalAmount: amount};
}
